import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider, useNotifications } from './context/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Rewards from './components/Rewards';
import Leaderboard from './components/Leaderboard';
import Camera from './components/Camera';
import CameraV2 from './components/CameraV2';
import CleaningCamera from './components/CleaningCamera';
import BeforeAfterCamera from './components/BeforeAfterCamera';
import Achievements from './components/Achievements';
import Profile from './components/Profile';
import ActivityFeed from './components/ActivityFeed';
import TopPosts from './components/TopPosts';
import Ranks from './components/Ranks';
import AdminPanel from './components/AdminPanel';
import BugReport from './components/BugReport';
import CampusGame from './components/CampusGamePerformant';
import IssueTracker from './components/IssueTracker';
import ThemeToggle from './components/ThemeToggle';
import NotificationContainer from './components/NotificationContainer';
import ToastContainer from './components/ToastContainer';
import InstallPrompt from './components/InstallPrompt';
import { useToast } from './hooks/useToast';
import { activityService } from './services/activityService';
import './App.css';

function AppContent() {
  const [currentPage, setCurrentPage] = useState('login');
  const { user, updatePoints, lastUnlockedAchievement, isInitialLoading, clearLastAchievement } = useAuth();
  const { showAchievement } = useNotifications();
  const { toasts, showToast, removeToast } = useToast();
  
  // Check if user is logged in based on user state
  const isLoggedIn = !!user;

  const handleLogin = () => {
    setCurrentPage('dashboard');
  };

  const handlePointsEarned = (points: number, activityType?: 'before-after' | 'video', location?: string) => {
    updatePoints(points, activityType, location);
  };
  
  // Set page based on user state
  useEffect(() => {
    if (user) {
      // Only redirect to dashboard if we're on a non-authenticated page
      const nonAuthPages = ['login', 'signup', 'forgot-password', 'reset-password'];
      if (nonAuthPages.includes(currentPage)) {
        setCurrentPage('dashboard');
      }
    } else if (!isInitialLoading && !user) {
      // Only redirect to login if we're not in initial loading and user is null
      // AND we're not already on an auth page
      const authPages = ['login', 'signup', 'forgot-password', 'reset-password'];
      if (!authPages.includes(currentPage)) {
        setCurrentPage('login');
      }
    }
  }, [user, isInitialLoading]);

  // Check URL parameters for password reset
  useEffect(() => {
    // Check hash parameters (Supabase uses hash fragments)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    
    // Check if this is a password reset flow
    if (type === 'recovery' && accessToken) {
      // Store tokens for the reset password component
      sessionStorage.setItem('recovery_access_token', accessToken);
      if (refreshToken) {
        sessionStorage.setItem('recovery_refresh_token', refreshToken);
      }
      setCurrentPage('reset-password');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Watch for new achievements
  useEffect(() => {
    if (lastUnlockedAchievement) {
      // Check if this achievement was already shown in this session
      const shownAchievements = sessionStorage.getItem('shownAchievements');
      const shownIds = shownAchievements ? JSON.parse(shownAchievements) : [];
      
      if (!shownIds.includes(lastUnlockedAchievement.id)) {
        showAchievement(lastUnlockedAchievement);
        // Mark as shown
        shownIds.push(lastUnlockedAchievement.id);
        sessionStorage.setItem('shownAchievements', JSON.stringify(shownIds));
      }
      
      // Clear the achievement after checking to prevent re-checking
      setTimeout(() => {
        clearLastAchievement();
      }, 100);
    }
  }, [lastUnlockedAchievement, showAchievement, clearLastAchievement]);

  // Storage buckets should be created manually in Supabase dashboard
  // Client-side bucket creation is restricted by RLS policies

  const renderPage = () => {
    if (!isLoggedIn) {
      if (currentPage === 'signup') {
        return <Signup onLogin={handleLogin} onBackToLogin={() => setCurrentPage('login')} />;
      } else if (currentPage === 'forgot-password') {
        return <ForgotPassword onBack={() => setCurrentPage('login')} />;
      } else if (currentPage === 'reset-password') {
        return <ResetPassword onSuccess={() => {
          setCurrentPage('login');
          // Clean up stored tokens
          sessionStorage.removeItem('recovery_access_token');
          sessionStorage.removeItem('recovery_refresh_token');
        }} />;
      }
      return <Login 
        onSignup={() => setCurrentPage('signup')} 
        onForgotPassword={() => setCurrentPage('forgot-password')} 
      />;
    }
    
    switch(currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={setCurrentPage} />;
      case 'rewards':
        return <Rewards onNavigate={setCurrentPage} showToast={showToast} />;
      case 'leaderboard':
        return <Leaderboard onNavigate={setCurrentPage} />;
      case 'camera':
        return <BeforeAfterCamera onNavigate={setCurrentPage} onPointsEarned={handlePointsEarned} />;
      case 'activities':
        return <ActivityFeed onNavigate={setCurrentPage} />;
      case 'achievements':
        return <Achievements onNavigate={setCurrentPage} />;
      case 'profile':
        return <Profile onNavigate={setCurrentPage} showToast={showToast} />;
      case 'feed':
        return <ActivityFeed onNavigate={setCurrentPage} />;
      case 'top':
        return <TopPosts onNavigate={setCurrentPage} />;
      case 'ranks':
        return <Ranks onNavigate={setCurrentPage} />;
      case 'admin':
        return <AdminPanel onNavigate={setCurrentPage} showToast={showToast} />;
      case 'bug-report':
        return <BugReport onNavigate={setCurrentPage} showToast={showToast} />;
      case 'game':
        return <CampusGame onNavigate={setCurrentPage} showToast={showToast} onPointsEarned={handlePointsEarned} />;
      case 'issue-tracker':
        return <IssueTracker onNavigate={setCurrentPage} />;
      default:
        return <Dashboard onNavigate={setCurrentPage} />;
    }
  };

  // Show loading screen while checking initial auth state
  if (isInitialLoading) {
    return (
      <div className="App">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column'
        }}>
          <div style={{
            fontSize: '48px',
            animation: 'spin 2s linear infinite',
            marginBottom: '20px'
          }}>🌱</div>
          <p style={{ fontSize: '18px', color: '#666' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {renderPage()}
      <ThemeToggle />
      <NotificationContainer />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <InstallPrompt />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </NotificationProvider>
    </ErrorBoundary>
  );
}

export default App
